import { TextSearchInput } from '@/app/components/TextSearchInput'
import { useSearchInputContext } from '@/app/modules/course/contexts/InputSearchContext'
import { useIntersectionObserver } from '@/app/modules/course/hooks/useIntersectionObserver'
import { useAuth } from '@/app/providers/Auth/Auth'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect, useRef } from 'react'

type CoursesHeaderProps = {
  onFilter: (term: string) => void
  onSearch: (searchTerm: string) => void
  wrapperProps?: React.HTMLProps<HTMLDivElement>
} & React.HTMLProps<HTMLDivElement>

export const CoursesHeader = ({
  wrapperProps,
  onSearch,
  onFilter,
  ...props
}: CoursesHeaderProps) => {
  const { firstName, isLoading } = useAuth()
  const t = useTranslations('Courses')
  const searchInputRef = useRef<HTMLDivElement>(null)
  const isVisible = useIntersectionObserver(searchInputRef)
  const { setIsLargeSearchVisible } = useSearchInputContext()

  useEffect(() => {
    setIsLargeSearchVisible(isVisible)
  }, [isVisible, setIsLargeSearchVisible])

  return (
    <div
      {...wrapperProps}
      className={`bg-gradient-to-b from-white to-[#FFF9F1] ${wrapperProps?.className ?? ''}`}
    >
      <div
        {...props}
        className={`flex flex-col gap-10 py-12 sm:py-20 ${props.className ?? ''}`}
      >
        {isLoading ? (
          <div className="animate-pulse font-title text-3xl md:text-4xl">
            <div className="h-10 w-full rounded-full bg-grey-background" />
          </div>
        ) : (
          <div className="font-title text-3xl md:text-4xl">
            {t('welcome', { name: firstName })} 👋🏼
          </div>
        )}
        <div className="text-base md:text-lg">{t('intro_message')}</div>
        <div className="grid grid-rows-1 gap-7">
          <div
            className="flex flex-row items-center gap-5 rounded-xl bg-mydra-light-purple p-8 md:h-[383px]"
            style={{ backgroundImage: 'url(/images/search-bg.png)' }}
          >
            <div className="flex flex-[5] flex-col gap-10">
              <div className="text-center font-title text-2xl md:text-4xl">
                {t('text_search_title')}
              </div>

              <div
                ref={searchInputRef}
                className="mx-auto w-full xs:max-w-[480px] sm:max-w-[580px]"
              >
                <TextSearchInput onSearch={onSearch} onFilterClick={onFilter} />
              </div>

              <div className="hidden flex-row flex-wrap items-center justify-center gap-2 md:flex md:gap-8">
                <Image
                  src="/images/henry-lg.png"
                  alt="Henry"
                  width={96}
                  height={45}
                  className="h-auto w-[38px] object-contain sm:w-[48px] md:w-[96px]"
                  priority
                />
                <Image
                  src="/images/hult-lg.png"
                  alt="Hult"
                  width={96}
                  height={45}
                  className="h-auto w-[38px] object-contain sm:w-[48px] md:w-[96px]"
                  priority
                />
                <Image
                  src="/images/IE-university-lg.png"
                  alt="IE University"
                  width={79}
                  height={47}
                  className="h-auto w-[30px] object-contain sm:w-[40px] md:w-[79px]"
                  priority
                />
                <Image
                  src="/images/integra-lg.png"
                  alt="Integra"
                  width={118}
                  height={72}
                  className="h-auto w-[49px] object-contain sm:w-[59px] md:w-[118px]"
                  priority
                />
                <Image
                  src="/images/iebs.png"
                  alt="IEBS"
                  width={118}
                  height={72}
                  className="h-auto w-[49px] object-contain sm:w-[59px] md:w-[118px]"
                  priority
                />
                <Image
                  src="/images/CEI-lg.png"
                  alt="CEI"
                  width={147}
                  height={93}
                  className="h-auto w-[25px] object-contain sm:w-[35px] md:w-[147px]"
                  priority
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
